import React, {useContext} from 'react';

import clsx from 'clsx';
import {CopyButton} from 'src/components/copy-button';
import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';

import {FlatButton} from '@pb/kit-component-button';
import {Dropdown} from '@pb/kit-component-dropdown';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';


import {getConnectionErrorMessage} from '../utils/getDuplicatesErrorMessage';

import {
    connectionStatusErrorMessage,
    connectionStatusStatusTitle,
    connectionStatusSuccessMessage,
} from './connection-status.module.styl';

import type {CredentialsUpdateResult} from 'src/openapi/admin/models/CredentialsUpdateResult';

type Props = {
    enabled: boolean,
    unlinkBusy: boolean,
    isTestReady: boolean,
    connectionStatus: CredentialsUpdateResult | null,
    onUnlink: () => void,
};

export const ConnectionStatus = (props: Props) => {
    const {setErrorMessage} = useContext(MessageContext);
    const {hasAccess} = useAdminAccessRight();

    const {
        enabled,
        connectionStatus,
        isTestReady,
        unlinkBusy,
        onUnlink,
    } = props;

    const connectionStatusClasses = clsx([
        connectionStatus?.type?.code === 'ok' && connectionStatusSuccessMessage,
        connectionStatus?.type?.code === 'error' && connectionStatusErrorMessage,
    ]);

    const unlinkDisabled = !hasAccess(ACCESS_RIGHTS.CRM_SCREEN_EDIT);

    if (enabled && connectionStatus?.status && isTestReady && connectionStatus?.status?.crmUserInfo?.orgId) {
        return (
            <div style={{width: 300}}>
                <Group gap="s">
                    {connectionStatus?.type?.code === 'ok' ? (
                        <Group
                            className={connectionStatusClasses}
                            justify="start"
                            gap="xs"
                            alignItems="center">
                            <Icon type={Icons.CHECK}/>
                            Connected
                        </Group>
                    ) : (
                        <div
                            className={connectionStatusStatusTitle}
                            onClick={() => setErrorMessage(getConnectionErrorMessage(connectionStatus))}>
                            <Group
                                className={connectionStatusClasses}
                                justify="start"
                                gap="xs"
                                alignItems="center">
                                <Icon type={Icons.ERROR}/>
                                Connected
                            </Group>
                        </div>
                    )}
                    <Group gap="none" style={{width: 150}}>
                        <Dropdown
                            hoverEnabled={true}
                            placement="right"
                            anchor={({anchorRef, getAnchorProps}) => (
                                <span ref={anchorRef} {...getAnchorProps()}>
                                    {connectionStatus?.status?.crmUserInfo?.orgId ? (
                                        <EllipsisTextHint title={connectionStatus.status.crmUserInfo.orgId}>
                                            (Org ID: {connectionStatus.status.crmUserInfo.orgId})
                                        </EllipsisTextHint>
                                    ) : (
                                        <span>(Org ID: --)</span>
                                    )}
                                </span>
                            )}>
                            <Group direction="v" gap="s" className="enable-selection">
                                <Group>
                                    <strong style={{width: 50}}>Org ID:</strong>
                                    {connectionStatus.status.crmUserInfo?.orgId || '--'}
                                    {connectionStatus.status.crmUserInfo?.orgId ? (
                                        <CopyButton
                                            copiedText={connectionStatus.status.crmUserInfo?.orgId}
                                            entityName="Org ID"
                                        />
                                    ) : null}
                                </Group>
                                <Group>
                                    <strong style={{width: 50}}>User ID:</strong>
                                    {connectionStatus.status.crmUserInfo?.userId || '--'}
                                    {connectionStatus.status.crmUserInfo?.userId ? (
                                        <CopyButton
                                            copiedText={connectionStatus.status.crmUserInfo?.userId}
                                            entityName="User ID"
                                        />
                                    ) : null}
                                </Group>
                                <Group>
                                    <strong style={{width: 50}}>Email:</strong>
                                    {connectionStatus.status.crmUserInfo?.userEmail || '--'}
                                    {connectionStatus.status.crmUserInfo?.userEmail ? (
                                        <CopyButton
                                            copiedText={connectionStatus.status.crmUserInfo?.userEmail}
                                            entityName="User email"
                                        />
                                    ) : null}
                                </Group>
                            </Group>
                        </Dropdown>
                    </Group>
                    {connectionStatus?.savedOrgId ? (
                        <FlatButton
                            disabled={unlinkDisabled}
                            busy={unlinkBusy}
                            onClick={onUnlink}>
                            <b>Unlink</b>
                        </FlatButton>
                    ) : null}
                </Group>
            </div>
        );
    }

    if (enabled && connectionStatus?.status && isTestReady && !connectionStatus?.status?.crmUserInfo?.orgId) {
        return (
            <Group>
                <div
                    className={connectionStatusStatusTitle}
                    onClick={() => setErrorMessage(connectionStatus?.status.message)}>
                    <Group
                        className={connectionStatusErrorMessage}
                        justify="start"
                        gap="xs"
                        alignItems="center">
                        <Icon type={Icons.ERROR}/>
                        Not connected
                    </Group>
                </div>
                {connectionStatus.savedOrgId ? (
                    <FlatButton
                        disabled={unlinkDisabled}
                        busy={unlinkBusy}
                        onClick={onUnlink}>
                        <b>Unlink</b>
                    </FlatButton>
                ) : null}
            </Group>
        );
    }

    return (
        <Group alignItems="center">
            <span className="disabled">Not configured</span>
            {connectionStatus?.savedOrgId ? (
                <FlatButton
                    disabled={unlinkDisabled}
                    busy={unlinkBusy}
                    onClick={onUnlink}>
                    <b>Unlink</b>
                </FlatButton>
            ) : null}
        </Group>
    );
};
